import { Button } from '@/design-system';
import { platformApi } from '@/entities/tenant';

import { adminCopy } from '../copy';
import { useImpersonationStore } from './store';

/**
 * "Je kijkt mee als … bij …"
 *
 * Deze balk staat vast bovenaan het scherm zolang er een impersonatiesessie
 * loopt. Hij is niet weg te klikken en hij is niet samen te vouwen: meekijken in
 * andermans omgeving mag nooit iets zijn waarvan je vergeet dat het aanstaat.
 * De enige uitweg is stoppen.
 *
 * Kleur is amber, niet rood. Rood betekent in dit ontwerp "geblokkeerd of te
 * laat"; dit is een toestand die aandacht vraagt, geen fout (principe 9).
 *
 * Hij hangt één keer in `AppShell`, boven de rail en het werkvlak, en reist zo
 * mee naar élk scherm - niet alleen naar het platformbeheer. Monteer hem niet
 * nog een keer in een scherm.
 */
export function ImpersonationBar() {
    const session = useImpersonationStore((state) => state.session);
    const stop = useImpersonationStore((state) => state.stop);

    if (!session) {
        return null;
    }

    async function handleStop(sessionUuid: string) {
        try {
            await platformApi.endImpersonation(sessionUuid);
        } finally {
            /* De balk gaat hoe dan ook uit: een mislukte call mag je niet in
               de veronderstelling laten dat je nog meekijkt. Het token verloopt
               daarnaast vanzelf. */
            stop();
        }
    }

    return (
        <>
            <div
                role="status"
                aria-live="polite"
                className="fixed inset-x-0 top-0 z-[70] flex flex-wrap items-center gap-3 border-b border-[var(--warn-border)] bg-[var(--warn-tint)] px-4 py-[10px]"
            >
                <span
                    aria-hidden="true"
                    className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--warn-field)]"
                />
                <p className="m-0 min-w-0 flex-1 text-[13px] leading-[1.4] font-medium text-[var(--warn-text)]">
                    {adminCopy.barText(session.userName, session.tenantName)}
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleStop(session.sessionUuid)}
                >
                    {adminCopy.barStop}
                </Button>
            </div>
            {/* Houdt de inhoud eronder vrij van de vaste balk. */}
            <div aria-hidden="true" className="h-[45px] shrink-0" />
        </>
    );
}
